import React, { useState, useEffect, useRef } from "react";

const GridOverlay = () => {
    const [documentHeight, setDocumentHeight] = useState("100vh");
    const isPageLoaded = useRef(false);
    const resizeTimerRef = useRef(null);
    const initialUpdateRef = useRef(false);
    const animationInProgressRef = useRef(false);
    const updateQueuedRef = useRef(false);

    useEffect(() => {
        const updateHeight = () => {
            if (animationInProgressRef.current) {
                updateQueuedRef.current = true;
                return;
            }

            requestAnimationFrame(() => {
                const footer = document.querySelector("footer");

                if (footer) {
                    const footerRect = footer.getBoundingClientRect();
                    const footerPosition = footerRect.top + window.scrollY;

                    // Set the height to stop right at the beginning of the footer
                    setDocumentHeight(`${footerPosition}px`);
                } else {
                    const body = document.body;
                    const html = document.documentElement;

                    const height = Math.max(
                        body.scrollHeight,
                        body.offsetHeight,
                        html.clientHeight,
                        html.scrollHeight,
                        html.offsetHeight
                    );

                    setDocumentHeight(`${height}px`);
                }
            });
        };

        const handleResize = () => {
            updateHeight();

            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
            resizeTimerRef.current = setTimeout(() => {
                updateHeight();
                setTimeout(updateHeight, 100);
                resizeTimerRef.current = null;
            }, 200);
        };

        const handleAccordionClick = () => {
            animationInProgressRef.current = true;

            setTimeout(() => {
                animationInProgressRef.current = false;

                if (updateQueuedRef.current) {
                    updateQueuedRef.current = false;
                    updateHeight();
                }
            }, 350);
        };

        const setupAccordionListeners = () => {
            const accordionButtons =
                document.querySelectorAll("[aria-expanded]");
            accordionButtons.forEach((button) => {
                button.addEventListener("click", handleAccordionClick);
            });

            return () => {
                accordionButtons.forEach((button) => {
                    button.removeEventListener("click", handleAccordionClick);
                });
            };
        };

        const handleTransitionEnd = (e) => {
            if (
                e.target.classList.contains("overflow-hidden") &&
                e.propertyName === "height"
            ) {
                updateHeight();
            }
        };

        if (document.readyState === "complete") {
            isPageLoaded.current = true;
            updateHeight();
            setTimeout(updateHeight, 100);
        } else {
            window.addEventListener("load", () => {
                isPageLoaded.current = true;
                updateHeight();
                setTimeout(updateHeight, 100);
            });
        }

        window.addEventListener("resize", handleResize);
        document.body.addEventListener("transitionend", handleTransitionEnd);
        const cleanupAccordionListeners = setupAccordionListeners();

        const observer = new MutationObserver(() => {
            cleanupAccordionListeners();
            setupAccordionListeners();

            if (!animationInProgressRef.current) {
                updateHeight();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        updateHeight();
        setTimeout(updateHeight, 200);

        const intervalCheck = setInterval(updateHeight, 2000);

        if (!initialUpdateRef.current) {
            initialUpdateRef.current = true;
            setTimeout(updateHeight, 300);
            setTimeout(updateHeight, 1000);
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("load", updateHeight);
            document.body.removeEventListener(
                "transitionend",
                handleTransitionEnd
            );
            cleanupAccordionListeners();
            observer.disconnect();
            clearInterval(intervalCheck);
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
        };
    }, []);

    return (
        <div
            className="absolute top-0 left-0 w-full pointer-events-none z-[1] overflow-hidden"
            id="grid"
            style={{ height: documentHeight }}
        >
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(230,230,230,0.12)_1px,transparent_1px)] bg-[length:50px_100%] sm:bg-[length:75px_100%] lg:bg-[length:100px_100%]"></div>
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(230,230,230,0.12)_1px,transparent_1px)] bg-[length:100%_50px] sm:bg-[length:100%_75px] lg:bg-[length:100%_100px]"></div>

            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(230,230,230,0.25)_1px,transparent_1px)] bg-[length:200px_100%] sm:bg-[length:300px_100%] lg:bg-[length:400px_100%]"></div>
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(230,230,230,0.25)_1px,transparent_1px)] bg-[length:100%_200px] sm:bg-[length:100%_300px] lg:bg-[length:100%_400px]"></div>

            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.05] via-transparent to-purple-500/[0.05]"></div>

            <div
                className="absolute inset-0"
                style={{
                    backgroundImage:
                        "radial-gradient(circle, rgba(180,180,180,0.2) 1px, transparent 1px)",
                    backgroundSize: "200px 200px",
                    backgroundPosition: "-100px -100px",
                }}
            ></div>
        </div>
    );
};

export default GridOverlay;
